import { useState } from "react";

import {
  ButtonProps,
  Button,
  Stack,
  Box,
  Text,
  Collapse,
  Skeleton,
  Tooltip,
  Group,
} from "@mantine/core";
import {
  IconBriefcase,
  IconBrain,
  IconSchool,
  IconTable,
  IconHelpCircle,
  IconChevronDown,
  IconChevronUp,
} from "@tabler/icons-react";
import BenchmarkingDashboardComprehensiveLogo from "app/lib/frontend/cm/pages/benchmarking-dashboard/_assets/benchmarking-dashboard-comprehensive.png";
import BenchmarkingDashboardComptryxLogo from "app/lib/frontend/cm/pages/benchmarking-dashboard/_assets/benchmarking-dashboard-comptryx.png";
import BenchmarkingDashboardMercerLogo from "app/lib/frontend/cm/pages/benchmarking-dashboard/_assets/benchmarking-dashboard-mercer.png";
import BenchmarkingDashboardSalaryLogo from "app/lib/frontend/cm/pages/benchmarking-dashboard/_assets/benchmarking-dashboard-salary.png";
import Image, { StaticImageData } from "next/image";
import Link from "next/link";
import { useRouter } from "next/router";

import { useTrackerAuth } from "components/AuthProvider";
import {
  EcsUpsellModal,
  useEcsUpsellModalDisclosure,
} from "components/EcsUpsellModal/EcsUpsellModal";
import { usePaidFeatureModalDisclosure, PaidFeatureModal } from "components/PaidFeatureModal";
import { useAuthorizedDataSets } from "hooks/useAuthorizedDataSets";
import { EXEC_COMP_DATASET } from "lib/frontend/cm/pages/vc/survey-results/_util/ExecCompDatasets";
import { CompanyType } from "prisma/cm/client";

const datasetTooltips = {
  comprehensive:
    "Search aggregated salary data from job postings of over 6,000 U.S. tech companies, made possible by pay transparency laws and refreshed daily.",
  [EXEC_COMP_DATASET.exec]:
    "VP-level, C-level, and direct reports to the CEO. Data reported by VC-backed companies.",
  [EXEC_COMP_DATASET.ai]:
    "Employees whose primary responsibilities are in artificial intelligence. Data reported by VC-backed companies.",
  [EXEC_COMP_DATASET.board]:
    "External board members or advisors who receive compensation for their board duties. Data reported by VC-backed companies.",
};

const navLinkProps: ButtonProps = {
  fullWidth: true,
  variant: "subtle",
  size: "sm",
  justify: "flex-start",
  color: "gray",
  pl: 16, // Add proper left padding for better checkbox spacing
};

const DatasetIcon = ({ iconSrc }: { iconSrc: StaticImageData }) => {
  return (
    <Box mr={2}>
      <Image src={iconSrc} alt={"carta"} height={16} width={16} />
    </Box>
  );
};

export const DatasetNavigation = (props: {
  activeDataset?: "comprehensive" | "mercer" | "comptryx" | "salary" | "carta" | "ecs";
  isGated?: boolean;
}) => {
  const { activeDataset, isGated } = props;
  const { user } = useTrackerAuth();
  const { datasets: authorizedDataSets, isLoading: isLoadingDatasets } = useAuthorizedDataSets();
  const [paidFeatureModalState, paidFeatureModalHandlers] = usePaidFeatureModalDisclosure();

  const isVcCompany = user?.company?.companyType === CompanyType.VC;

  if (isLoadingDatasets && !isGated) {
    return (
      <Box>
        <Stack>
          <Text size="xxs" c="dimmed" fw={600}>
            Loading datasets...
          </Text>
          <Skeleton height={30} />
          <Skeleton height={30} />
          <Skeleton height={30} />
        </Stack>
      </Box>
    );
  }

  return (
    <Box>
      <Stack>
        <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
          Datasets
        </Text>

        <DatasetComprehensiveNavigation
          withEcs={authorizedDataSets.ecs || isGated}
          activeDataset={
            activeDataset as "comprehensive" | "mercer" | "comptryx" | "salary" | "carta" | "ecs"
          }
          isGated={isGated || false}
        />

        {!isVcCompany && (
          <>
            <Button
              component={Link}
              href="/benchmarking/mercer"
              bg={activeDataset === "mercer" ? "gray.2" : undefined}
              leftSection={<DatasetIcon iconSrc={BenchmarkingDashboardMercerLogo} />}
              {...navLinkProps}
              onClick={(e) => {
                if (!authorizedDataSets.mercer || isGated) {
                  e.preventDefault();
                  paidFeatureModalHandlers.open("benchmarking");
                }
              }}
            >
              Mercer
            </Button>

            <Button
              component={Link}
              bg={activeDataset === "comptryx" ? "gray.2" : undefined}
              href="/benchmarking/comptryx"
              leftSection={<DatasetIcon iconSrc={BenchmarkingDashboardComptryxLogo} />}
              onClick={(e) => {
                if (!authorizedDataSets.comptryx || isGated) {
                  e.preventDefault();
                  paidFeatureModalHandlers.open("benchmarking");
                }
              }}
              {...navLinkProps}
            >
              Comptryx
            </Button>

            <Button
              component={Link}
              bg={activeDataset === "salary" ? "gray.2" : undefined}
              href="/benchmarking/salary"
              leftSection={<DatasetIcon iconSrc={BenchmarkingDashboardSalaryLogo} />}
              onClick={(e) => {
                if (!authorizedDataSets.salary || isGated) {
                  e.preventDefault();
                  paidFeatureModalHandlers.open("benchmarking");
                }
              }}
              {...navLinkProps}
            >
              Salary.com
            </Button>
          </>
        )}
      </Stack>
      <PaidFeatureModal {...paidFeatureModalState} onClose={paidFeatureModalHandlers.close} />
    </Box>
  );
};

function DatasetComprehensiveNavigation({
  withEcs,
  activeDataset,
  isGated = false,
}: {
  withEcs?: boolean;
  activeDataset: "comprehensive" | "mercer" | "comptryx" | "salary" | "carta" | "ecs";
  isGated: boolean;
}) {
  const [isComprehensiveOpen, setIsComprehensiveOpen] = useState(true);
  const router = useRouter();

  const [ecsUpsellModalState, ecsUpsellModalHandlers] = useEcsUpsellModalDisclosure();

  const handleGatedClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (isGated) {
      e.preventDefault();
      ecsUpsellModalHandlers.open("survey-results");
    }
  };

  if (!withEcs) {
    return (
      <Button
        component={Link}
        href="/benchmarking/s"
        bg={activeDataset === "comprehensive" ? "gray.2" : undefined}
        leftSection={<DatasetIcon iconSrc={BenchmarkingDashboardComprehensiveLogo} />}
        {...navLinkProps}
      >
        Comprehensive
      </Button>
    );
  }

  const isEcsPath =
    router.query.dataset === "ecs" ||
    (router.query.ecsDataset &&
      ["exec", "ai", "board"].includes(router.query.ecsDataset as string));

  return (
    <>
      <Button
        leftSection={<DatasetIcon iconSrc={BenchmarkingDashboardComprehensiveLogo} />}
        rightSection={
          isComprehensiveOpen ? <IconChevronDown size={16} /> : <IconChevronUp size={16} />
        }
        onClick={() => setIsComprehensiveOpen(!isComprehensiveOpen)}
        {...navLinkProps}
      >
        Comprehensive
      </Button>
      <Collapse in={isComprehensiveOpen}>
        <Stack gap={8} ml={12}>
          <Button
            component={Link}
            href="/benchmarking/ecs?ecsDataset=exec"
            {...navLinkProps}
            bg={isEcsPath && router.query.ecsDataset === "exec" ? "gray.2" : undefined}
            leftSection={<IconBriefcase size={16} />}
            size="sm"
            onClick={handleGatedClick}
          >
            <Group>
              Executive roles
              <Tooltip
                label={datasetTooltips[EXEC_COMP_DATASET.exec]}
                multiline
                w={250}
                position="right"
                withArrow
                zIndex={1000000}
              >
                <IconHelpCircle size={14} />
              </Tooltip>
            </Group>
          </Button>
          <Button
            component={Link}
            href="/benchmarking/ecs?ecsDataset=ai"
            {...navLinkProps}
            bg={isEcsPath && router.query.ecsDataset === "ai" ? "gray.2" : undefined}
            leftSection={<IconBrain size={16} />}
            size="sm"
            onClick={handleGatedClick}
          >
            <Group>
              AI roles
              <Tooltip
                label={datasetTooltips[EXEC_COMP_DATASET.ai]}
                multiline
                w={250}
                position="right"
                withArrow
                zIndex={1000000}
              >
                <IconHelpCircle size={14} />
              </Tooltip>
            </Group>
          </Button>
          <Button
            component={Link}
            href="/benchmarking/ecs?ecsDataset=board"
            {...navLinkProps}
            bg={isEcsPath && router.query.ecsDataset === "board" ? "gray.2" : undefined}
            leftSection={<IconSchool size={16} />}
            size="sm"
            onClick={handleGatedClick}
          >
            <Group>
              Board and advisory
              <Tooltip
                label={datasetTooltips[EXEC_COMP_DATASET.board]}
                multiline
                w={250}
                position="right"
                withArrow
                zIndex={1000000}
              >
                <IconHelpCircle size={14} />
              </Tooltip>
            </Group>
          </Button>
          <Button
            component={Link}
            href="/benchmarking/s"
            bg={!isGated && activeDataset === "comprehensive" ? "gray.2" : undefined}
            leftSection={<IconTable size={16} />}
            {...navLinkProps}
            onClick={handleGatedClick}
          >
            <Group justify="flex-start">
              Job postings data
              <Tooltip
                label={datasetTooltips.comprehensive}
                multiline
                w={250}
                position="right"
                withArrow
                zIndex={1000000}
              >
                <IconHelpCircle size={14} />
              </Tooltip>
            </Group>
          </Button>
        </Stack>
        <EcsUpsellModal {...ecsUpsellModalState} onClose={ecsUpsellModalHandlers.close} />
      </Collapse>
    </>
  );
}
